// src/components/federation/CMSRouteWrapper.tsx
import React, { Suspense, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import { useQueryClient, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

// Define CMS Router Props interface
interface CMSRouterProps {
  isExternal?: boolean;
  theme?: Theme;
  queryClient: QueryClient;
}

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; onReset?: () => void; moduleName?: string },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; onReset?: () => void; moduleName?: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('CMS Module Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          minHeight: 300
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            {this.props.moduleName || 'Module'} failed to load
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              this.setState({ hasError: false, error: undefined });
              this.props.onReset?.();
            }}
          >
            Retry
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Create a component for loading state
const LoadingFallback = ({ message = "Loading CMS module..." }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300
  }}>
    <CircularProgress size={32} sx={{ mb: 2 }} />
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);

interface LazyRouterLoaderProps {
  theme: Theme;
  queryClient: QueryClient;
}

// Wrapper to handle the lazy loading with error detection
const LazyRouterLoader = ({ theme, queryClient }: LazyRouterLoaderProps) => {
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [CMSRouter, setCMSRouter] = useState<React.ComponentType<CMSRouterProps> | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const loadRouter = async () => {
      try {
        setLoading(true);
        console.log('Loading CMS Router (attempt ' + (loadAttempt + 1) + ')');

        // Dynamic import with explicit error handling
        const module = await import('smart_cms/CMSRouter')
          .then(module => {
            console.log('CMS Router loaded successfully');
            return module.default;
          })
          .catch(error => {
            console.error('Failed to load CMS Router:', error);
            throw error;
          });

        setCMSRouter(() => module as React.ComponentType<CMSRouterProps>);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        // Error will be caught by ErrorBoundary
        throw error;
      }
    };

    loadRouter();
  }, [loadAttempt]);

  // Log current location for debugging
  useEffect(() => {
    console.log('CMSRouteWrapper location:', location.pathname);
  }, [location.pathname]);

  if (loading) {
    return <LoadingFallback />;
  }

  if (!CMSRouter) {
    throw new Error('Failed to load CMS Router');
  }

  // Use createElement to avoid JSX type issues with dynamic components
  return React.createElement(CMSRouter, {
    isExternal: true,
    theme,
    queryClient
  });
};

export const CMSRouteWrapper: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient(); // Get the QueryClient from the shell app
  const [retryCounter, setRetryCounter] = useState(0);

  const handleReset = () => {
    // Increment retry counter to force re-mounting the component
    setRetryCounter(prev => prev + 1);
  };

  console.log('CMSRouteWrapper rendering');

  return (
    <ErrorBoundary
      onReset={handleReset}
      moduleName="CMS Module"
    >
      <Suspense fallback={<LoadingFallback />}>
        <Box key={retryCounter} sx={{ width: '100%', height: '100%' }}>
          {/* Wrap with QueryClientProvider to ensure CMS has access to shared QueryClient */}
          <QueryClientProvider client={queryClient}>
            <LazyRouterLoader theme={theme} queryClient={queryClient} />
          </QueryClientProvider>
        </Box>
      </Suspense>
    </ErrorBoundary>
  );
};

export default CMSRouteWrapper;
